import { usePreviewAssociatedObjects } from 'customer-data-associations';
import { deriveNextLastActivities, derivePrefixFromEngagementType } from '../utils/nextLastActivityUtils';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { ExclamationIcon } from 'foundations-assets';
import styled from 'styled-components';
// @ts-expect-error Untyped import
import FormattedDuration from 'I18n/components/FormattedDuration';
import { DEAL_TYPE_ID, TICKET_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { ShinySkeletonText } from '../utils/ShinySkeletonText.tsx';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledActivitySpan = styled.span.withConfig({
  displayName: "NextLastActivity__StyledActivitySpan",
  componentId: "sc-1b7s3up-0"
})(({
  theme
}) => `
  font-size: ${theme.typography['detail-100'].fontSize};
  font-weight: ${theme.typography['display-100'].fontWeight};
`);
const GENERAL_DEAL_TO_ENGAGEMENT_ASSOCIATION_ID = '0-11';
const GENERAL_TICKET_TO_ENGAGEMENT_ASSOCIATION_ID = '0-17';
const OBJECT_TYPE_ID_TO_ASSOCIATION_MAP = {
  [DEAL_TYPE_ID]: GENERAL_DEAL_TO_ENGAGEMENT_ASSOCIATION_ID,
  [TICKET_TYPE_ID]: GENERAL_TICKET_TO_ENGAGEMENT_ASSOCIATION_ID
};
export const NextLastActivity = ({
  notesNextActivityDate,
  notesLastUpdated,
  objectId,
  isNextLastActivityTurnedOn,
  isInactive,
  createDate,
  associationsForCurrentType,
  objectTypeId,
  getAssociationData
}) => {
  const engagementAssociationDefinition = associationsForCurrentType[OBJECT_TYPE_ID_TO_ASSOCIATION_MAP[objectTypeId]];
  const hasCustomAssociationGetter = typeof getAssociationData === 'function';
  const associatedObjectsQuery = usePreviewAssociatedObjects({
    objectId,
    associationDefinition: engagementAssociationDefinition,
    skip: hasCustomAssociationGetter
  });
  const associatedObjects = hasCustomAssociationGetter ? {
    data: getAssociationData(objectId)
  } : associatedObjectsQuery;
  const engagements = associatedObjects && associatedObjects.data ? associatedObjects.data : [];
  const isAssociatedObjectsLoading = associatedObjects.loading;
  if (isAssociatedObjectsLoading) {
    return /*#__PURE__*/_jsxs(TFlex, {
      direction: "column",
      children: [/*#__PURE__*/_jsx(StyledActivitySpan, {
        "data-test-id": "cdbc-next-activity-loading",
        children: /*#__PURE__*/_jsx(ShinySkeletonText, {
          isInactive: isInactive
        })
      }), isNextLastActivityTurnedOn && /*#__PURE__*/_jsx(StyledActivitySpan, {
        children: /*#__PURE__*/_jsx(ShinySkeletonText, {
          isInactive: isInactive
        })
      })]
    });
  }
  const {
    nextActivity,
    lastActivity
  } = deriveNextLastActivities({
    notesNextActivityDate,
    notesLastUpdated,
    engagements
  });
  const showLastActivity = (isNextLastActivityTurnedOn || isInactive) && lastActivity; // eg Call 14 Days Ago

  const showNoActivity = (isNextLastActivityTurnedOn || isInactive) && !lastActivity && createDate;
  // eg No activity for 14 days

  const showNextActivity = isNextLastActivityTurnedOn && nextActivity; // eg Call in 14 days

  const showNoActivityScheduled = isNextLastActivityTurnedOn && !nextActivity; // eg No activity schedule

  return /*#__PURE__*/_jsxs(TFlex, {
    direction: "column",
    children: [showLastActivity && /*#__PURE__*/_jsxs(StyledActivitySpan, {
      "data-test-id": "cdbc-last-activity",
      children: [derivePrefixFromEngagementType({
        engagementType: lastActivity.properties.hs_engagement_type.value,
        isNextActivity: false
      }), ' ', /*#__PURE__*/_jsx(FormattedDuration, {
        to: new Date(Number(lastActivity.properties.hs_timestamp.value)),
        from: new Date(),
        includeAffix: true
      })]
    }), showNoActivity && /*#__PURE__*/_jsx(StyledActivitySpan, {
      "data-test-id": "cdbc-no-activity-for-x-time",
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "customer-data-board-card.nextLastActivity.noActivityForTime",
        options: {
          time: /*#__PURE__*/_jsx(FormattedDuration, {
            to: new Date(Number(createDate)),
            from: new Date()
          })
        }
      })
    }), showNextActivity && /*#__PURE__*/_jsxs(StyledActivitySpan, {
      "data-test-id": "cdbc-next-activity",
      children: [derivePrefixFromEngagementType({
        engagementType: nextActivity.properties.hs_engagement_type.value,
        isNextActivity: true
      }), ' ', /*#__PURE__*/_jsx(FormattedDuration, {
        from: new Date(),
        to: new Date(Number(nextActivity.properties.hs_timestamp.value)),
        includeAffix: true
      })]
    }), showNoActivityScheduled && /*#__PURE__*/_jsxs(StyledActivitySpan, {
      "data-test-id": "cdbc-no-activity-scheduled",
      children: [/*#__PURE__*/_jsx(ExclamationIcon, {
        color: "icon-tertiary-default"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-data-board-card.nextLastActivity.noActivityScheduled"
      })]
    })]
  });
};